import React, { useEffect, useMemo, useState } from "react";

const sma = (candles, p) =>
  candles.map((_, i) =>
    i < p - 1 ? null : candles.slice(i - p + 1, i + 1).reduce((s, c) => s + c.close, 0) / p
  );

const timeOf = (c) => c.time || c.timestamp || c.date || c.ts || c.datetime;
const timeMsOf = (value) => {
  if (value == null || value === "") return NaN;
  const n = Number(value);
  if (Number.isFinite(n)) return n;
  const d = new Date(value).getTime();
  return Number.isFinite(d) ? d : NaN;
};

const formatTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return String(value);
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

export default function CandleChart({ candles = [], height = 260, width = 760, mode = "line", markers = [], onViewChange }) {
  const W = Math.max(520, Number(width) || 760);
  const H = height;
  const PAD = { top: 10, right: 62, bottom: 52, left: 24 };
  const [windowPct, setWindowPct] = useState([0, 1]);
  const [dragState, setDragState] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);

  const normalized = useMemo(() => candles
    .map((c) => ({
      ...c,
      open: Number(c.open),
      high: Number(c.high),
      low: Number(c.low),
      close: Number(c.close),
      timeMs: timeMsOf(timeOf(c)),
    }))
    .filter((c) => [c.open, c.high, c.low, c.close].every(Number.isFinite)), [candles]);

  const candleWindowKey = useMemo(() => {
    if (!normalized.length) return "empty";
    const first = normalized[0]?.timeMs || "";
    const last = normalized[normalized.length - 1]?.timeMs || "";
    return `${normalized.length}:${first}:${last}`;
  }, [normalized]);

  useEffect(() => {
    setWindowPct([0, 1]);
    setHoverIndex(null);
    setDragState(null);
  }, [candleWindowKey]);

  const total = normalized.length;
  const viewStart = Math.floor(windowPct[0] * Math.max(total - 1, 0));
  const viewEnd = Math.max(viewStart + 1, Math.ceil(windowPct[1] * total));
  const slice = useMemo(() => normalized.slice(viewStart, viewEnd), [normalized, viewStart, viewEnd]);

  const { minP, maxP, sma20, sma50 } = useMemo(() => {
    if (!slice.length) return { minP: 0, maxP: 1, sma20: [], sma50: [] };
    const timedSlice = slice.filter((c) => Number.isFinite(c.timeMs));
    const firstMs = timedSlice[0]?.timeMs;
    const lastMs = timedSlice[timedSlice.length - 1]?.timeMs;
    const markerPrices = markers
      .filter((m) => {
        const markerMs = timeMsOf(m.time);
        if (!Number.isFinite(markerMs) || !Number.isFinite(firstMs) || !Number.isFinite(lastMs)) return true;
        return markerMs >= firstMs && markerMs <= lastMs;
      })
      .map((m) => Number(m.price))
      .filter(Number.isFinite);
    const all = slice.flatMap(c => [c.high, c.low]).concat(markerPrices);
    const min = Math.min(...all);
    const max = Math.max(...all);
    const pad = Math.max((max - min) * 0.08, max * 0.002, 1);
    return {
      minP: min - pad,
      maxP: max + pad,
      sma20: sma(slice, 20),
      sma50: sma(slice, 50),
    };
  }, [slice, markers]);

  const sy = v => PAD.top + (1 - (v - minP) / (maxP - minP || 1)) * (H - PAD.top - PAD.bottom);
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const bw = plotW / Math.max(slice.length - 1, 1);
  const xAt = (i) => PAD.left + i * bw;
  const xAtTime = (ms) => {
    if (!Number.isFinite(ms) || slice.length < 2 || !slice.every((c) => Number.isFinite(c.timeMs))) return null;
    const firstMs = slice[0].timeMs;
    const lastMs = slice[slice.length - 1].timeMs;
    if (ms < firstMs || ms > lastMs || firstMs === lastMs) return null;
    for (let i = 1; i < slice.length; i += 1) {
      const prev = slice[i - 1];
      const next = slice[i];
      if (ms <= next.timeMs) {
        const span = next.timeMs - prev.timeMs || 1;
        const frac = clamp((ms - prev.timeMs) / span, 0, 1);
        return xAt(i - 1) + frac * (xAt(i) - xAt(i - 1));
      }
    }
    return xAt(slice.length - 1);
  };

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(t => minP + t * (maxP - minP));

  const smaPoints = (arr) =>
    arr.map((v, i) => v ? `${xAt(i)},${sy(v)}` : null)
       .filter(Boolean).join(" ");

  const closePoints = slice.map((c, i) => `${xAt(i)},${sy(c.close)}`).join(" ");
  const areaPoints = slice.length
    ? `${PAD.left},${H - PAD.bottom} ${closePoints} ${xAt(slice.length - 1)},${H - PAD.bottom}`
    : "";
  const hover = hoverIndex == null ? null : slice[hoverIndex];
  const hoverX = hoverIndex == null ? null : xAt(hoverIndex);
  const hoverY = hover ? sy(hover.close) : null;
  const brushTop = H - 28;
  const brushLeft = PAD.left;
  const brushW = plotW;
  const selX = brushLeft + windowPct[0] * brushW;
  const selW = Math.max((windowPct[1] - windowPct[0]) * brushW, 8);
  const resolvedMarkers = useMemo(() => {
    if (!slice.length) return [];
    return markers
      .map((marker) => {
        const price = Number(marker.price);
        const markerMs = timeMsOf(marker.time);
        const hasUsableTime = Number.isFinite(markerMs) && slice.some((c) => Number.isFinite(c.timeMs));
        const exactX = xAtTime(markerMs);
        let nearest = -1;
        if (hasUsableTime && exactX != null) {
          let bestDiff = Infinity;
          slice.forEach((c, i) => {
            if (!Number.isFinite(c.timeMs)) return;
            const diff = Math.abs(c.timeMs - markerMs);
            if (diff < bestDiff) {
              bestDiff = diff;
              nearest = i;
            }
          });
        }
        return {
          ...marker,
          price,
          index: nearest,
          x: exactX ?? (!hasUsableTime && nearest >= 0 ? xAt(nearest) : null),
          y: Number.isFinite(price) ? sy(price) : null,
          visibleAtTime: exactX != null,
        };
      })
      .filter((m) => !Number.isFinite(timeMsOf(m.time)) || m.visibleAtTime);
  }, [markers, slice, bw, minP, maxP]);

  useEffect(() => {
    if (!onViewChange || slice.length < 2) return;
    const first = slice[0];
    const last = slice[slice.length - 1];
    const start = Number(first.close);
    const end = Number(last.close);
    onViewChange({
      start,
      end,
      change: end - start,
      changePct: start > 0 ? ((end - start) / start) * 100 : null,
      startTime: timeOf(first),
      endTime: timeOf(last),
      count: slice.length,
    });
  }, [onViewChange, slice]);

  const eventBox = (e) => {
    const svg = e.currentTarget.ownerSVGElement || e.currentTarget;
    return svg.getBoundingClientRect();
  };

  const pointerPct = (e) => {
    const box = eventBox(e);
    const x = (e.clientX - box.left) / box.width * W;
    return clamp((x - brushLeft) / brushW, 0, 1);
  };

  const startDrag = (kind, e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDragState({ kind, startPct: pointerPct(e), startWindow: windowPct });
  };

  const moveBrush = (e) => {
    if (!dragState) return;
    const pct = pointerPct(e);
    const delta = pct - dragState.startPct;
    const [a, b] = dragState.startWindow;
    const minWidth = Math.min(0.08, 10 / Math.max(total, 1));
    if (dragState.kind === "left") {
      setWindowPct([clamp(pct, 0, b - minWidth), b]);
    } else if (dragState.kind === "right") {
      setWindowPct([a, clamp(pct, a + minWidth, 1)]);
    } else {
      const width = b - a;
      const nextA = clamp(a + delta, 0, 1 - width);
      setWindowPct([nextA, nextA + width]);
    }
  };

  const handleChartMove = (e) => {
    const box = eventBox(e);
    const x = (e.clientX - box.left) / box.width * W;
    if (x < PAD.left || x > W - PAD.right || !slice.length) {
      setHoverIndex(null);
      return;
    }
    setHoverIndex(clamp(Math.round((x - PAD.left) / bw), 0, slice.length - 1));
  };

  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${W} ${H}`}
      style={{ display: "block", maxWidth: "100%", userSelect: "none", touchAction: "none" }}
      onPointerMove={(e) => dragState ? moveBrush(e) : handleChartMove(e)}
      onPointerUp={() => setDragState(null)}
      onPointerCancel={() => setDragState(null)}
      onPointerLeave={() => {
        if (!dragState) setHoverIndex(null);
      }}
    >
      <defs>
        <linearGradient id="chartAreaFill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#38bdf8" stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Grid */}
      {yTicks.map((v, i) => (
        <g key={i}>
          <line x1={PAD.left} x2={W - PAD.right} y1={sy(v)} y2={sy(v)} stroke="#0f172a" strokeWidth="1" />
          <text x={W - PAD.right + 8} y={sy(v) + 4} fill="#64748b" fontSize="10">{v.toFixed(2)}</text>
        </g>
      ))}
      {slice.length > 1 && (
        <>
          <polygon points={areaPoints} fill="url(#chartAreaFill)" />
          <polyline fill="none" stroke="#38bdf8" strokeWidth="2.2" points={closePoints} />
        </>
      )}
      {/* SMA20 */}
      {slice.length >= 20 && (
        <polyline fill="none" stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="4,3" points={smaPoints(sma20)} opacity="0.85" />
      )}
      {/* SMA50 */}
      {slice.length >= 50 && (
        <polyline fill="none" stroke="#818cf8" strokeWidth="1.5" strokeDasharray="4,3" points={smaPoints(sma50)} opacity="0.85" />
      )}
      {/* Candles */}
      {mode === "candles" && slice.map((c, i) => {
          const cx = xAt(i);
          const cw = Math.max(Math.min(bw * 0.55, 8), 2);
          const bull = c.close >= c.open;
          const col = bull ? "#22c55e" : "#ef4444";
          const bodyTop = sy(Math.max(c.open, c.close));
          const bodyH = Math.max(1, Math.abs(sy(c.open) - sy(c.close)));
          return (
            <g key={i}>
              <line x1={cx} x2={cx} y1={sy(c.high)} y2={sy(c.low)} stroke={col} strokeWidth="1" opacity="0.65" />
              <rect x={cx - cw / 2} y={bodyTop} width={cw} height={bodyH} fill={col} opacity="0.86" rx="0.5" />
            </g>
          );
        })}
      {resolvedMarkers.map((m, i) => {
        const color = m.color || (m.type === "entry" ? "#22c55e" : "#ef4444");
        const label = m.label || (m.type === "entry" ? "BUY" : "SELL");
        const labelX = m.x == null ? PAD.left + 4 : clamp(m.x + 7, PAD.left + 4, W - PAD.right - 64);
        const labelY = Number.isFinite(m.y) ? clamp(m.y - 18, PAD.top + 2, H - PAD.bottom - 22) : PAD.top + 8 + i * 22;
        return (
          <g key={`${label}-${i}`}>
            {m.x != null && (
              <line x1={m.x} x2={m.x} y1={PAD.top} y2={H - PAD.bottom} stroke={color} strokeDasharray="5,4" strokeWidth="1.4" opacity="0.85" />
            )}
            {Number.isFinite(m.y) && (
              <>
                <line x1={PAD.left} x2={W - PAD.right} y1={m.y} y2={m.y} stroke={color} strokeDasharray="2,4" strokeWidth="1" opacity="0.5" />
                <circle cx={m.x ?? PAD.left + 8} cy={m.y} r="4" fill={color} stroke="#020617" strokeWidth="2" />
              </>
            )}
            <rect x={labelX} y={labelY} width="64" height="18" fill="#020617" stroke={color} rx="5" opacity="0.96" />
            <text x={labelX + 6} y={labelY + 12} fill={color} fontSize="9" fontWeight="700">
              {label} {Number.isFinite(m.price) ? m.price.toFixed(2) : ""}
            </text>
          </g>
        );
      })}
      {hover && (
        <g>
          <line x1={hoverX} x2={hoverX} y1={PAD.top} y2={H - PAD.bottom} stroke="#64748b" strokeDasharray="3,3" />
          <line x1={PAD.left} x2={W - PAD.right} y1={hoverY} y2={hoverY} stroke="#64748b" strokeDasharray="3,3" />
          <circle cx={hoverX} cy={hoverY} r="4" fill="#38bdf8" stroke="#020617" strokeWidth="2" />
          <rect x={Math.min(hoverX + 8, W - 168)} y={Math.max(PAD.top, hoverY - 34)} width="160" height="42" fill="#020617" stroke="#1e293b" rx="6" />
          <text x={Math.min(hoverX + 18, W - 158)} y={Math.max(PAD.top + 15, hoverY - 17)} fill="#94a3b8" fontSize="10">{formatTime(timeOf(hover))}</text>
          <text x={Math.min(hoverX + 18, W - 158)} y={Math.max(PAD.top + 32, hoverY)} fill="#e2e8f0" fontSize="12" fontWeight="700">Rs {hover.close.toFixed(2)}</text>
        </g>
      )}
      {/* Legend */}
      <g transform={`translate(${PAD.left},${H - 36})`}>
        <rect width="8" height="3" fill="#f59e0b" rx="1" />
        <text x="11" y="3" fill="#64748b" fontSize="8">SMA20</text>
        <rect x="48" width="8" height="3" fill="#818cf8" rx="1" />
        <text x="59" y="3" fill="#64748b" fontSize="8">SMA50</text>
        <rect x="96" width="8" height="3" fill="#38bdf8" rx="1" />
        <text x="107" y="3" fill="#64748b" fontSize="8">Price</text>
      </g>
      {total > 2 && (
        <g>
          <line x1={brushLeft} x2={brushLeft + brushW} y1={brushTop + 8} y2={brushTop + 8} stroke="#1e293b" strokeWidth="8" strokeLinecap="round" />
          <rect
            x={selX}
            y={brushTop}
            width={selW}
            height="16"
            fill="#0d1f35"
            stroke="#38bdf8"
            rx="4"
            opacity="0.95"
            onPointerDown={(e) => startDrag("move", e)}
            style={{ cursor: "grab" }}
          />
          <rect x={selX - 4} y={brushTop - 2} width="8" height="20" fill="#38bdf8" rx="3" onPointerDown={(e) => startDrag("left", e)} style={{ cursor: "ew-resize" }} />
          <rect x={selX + selW - 4} y={brushTop - 2} width="8" height="20" fill="#38bdf8" rx="3" onPointerDown={(e) => startDrag("right", e)} style={{ cursor: "ew-resize" }} />
        </g>
      )}
    </svg>
  );
}
